import { Box, Spacer, TextInput, TextNew } from '@jfs/ui-new'
import { FC, useEffect, useState } from 'react'
import {
  convertDateToYYYYMMDD,
  convertFrom12To24Format,
  onlyDigit,
  returnFormattedTime,
  toLocaleString
} from '../../util/util'
import { renderCalendarPortal, renderTimerPortal } from './DOMPortals'
import { getPresentMinutes, timeWithoutMeridian, updateMeridianFormat } from './FutureDate'
import { styles } from './styles'
import { findExpiryChecked, timerValue, updateTimeIfWrongInput } from './TimePickerHelperFunctions'
import { TimePickerProps } from './TimerInterface'
import { renderArrowKeys, renderTimer } from './TimerUISeparator'

const TimePicker: FC<TimePickerProps> = ({ allValues, setFieldValue, fieldName }) => {
  const [date, updateDate] = useState(toLocaleString(new Date(Date.now()).toUTCString()))
  const [hour, setHours] = useState(timeWithoutMeridian)
  const [minute, setMinutes] = useState(getPresentMinutes)
  const [meridian, updateMeridian] = useState(updateMeridianFormat)
  const [layoutValue, setLayoutValue] = useState({})
  const [layoutValueCalendar, setLayoutValueOfCalendar] = useState({})
  const [showModal, setModalVisibility] = useState(false)
  const [isCalendarVisible, setCalendarVisible] = useState(false)
  const [focused, setFocused] = useState(false)

  const time = `${Number(hour)}:${Number(minute)}:${meridian}`
  const selectedTime = `${convertDateToYYYYMMDD(date)}${convertFrom12To24Format(time)}`

  const isExpiryChecked = findExpiryChecked(allValues)

  const rendeTimerDropdown = (type: string) => {
    const isHour = type === 'hour'
    const isMinute = type === 'minute'
    const isMeridian = type === 'meridian'

    const onChangeText = (text: string) => {
      const isRegexPassed = onlyDigit.test(text) === true
      if (isHour && isRegexPassed) {
        setHours(text)
      } else if (isMinute && isRegexPassed) {
        setMinutes(text)
      }
    }

    return (
      <Box backgroundColor="WHITE">
        {renderArrowKeys('up', type, Number(hour), setHours, Number(minute), setMinutes, meridian, updateMeridian)}
        <Box alignSelf="center" maxHeight={25} maxWidth={25} justifyContent="center">
          <TextInput
            keyboardType={'decimal-pad'}
            onChangeText={(text: string) => onChangeText(text)}
            value={timerValue(isHour, isMinute, focused, hour, minute, meridian)}
            autoCompleteType="off"
            onFocus={() => setFocused(true)}
            onBlur={() => [
              setFocused(false),
              updateTimeIfWrongInput(Number(hour), Number(minute), setHours, setMinutes),
            ]}
            textAlign="center"
            onFocusBottomLineColor="TRANSPARENT"
            fontSize={14}
            autoFocus={false}
            maxLength={2}
            editable={!isMeridian}
            bottomLineColor="TRANSPARENT"
          />
        </Box>
        {renderArrowKeys('down', type, Number(hour), setHours, Number(minute), setMinutes, meridian, updateMeridian)}
      </Box>
    )
  }

  const handleLayoutChange = (event: any, comp: string) => {
    const isTimer = comp === 'timer'
    if (isTimer) {
      setLayoutValue(event?.nativeEvent?.layout)
    } else {
      setLayoutValueOfCalendar(event?.nativeEvent?.layout)
    }
  }

  const renderCalendar = () => {
    return (
      <Box
        borderWidth={1}
        flexDirection="row"
        onLayout={(event) => handleLayoutChange(event, 'calendar')}
        borderRadius={6}
        underlayColor="TRANSPARENT"
        onPress={() => setCalendarVisible(true)}
        testID={'calendarVisible'}
        borderColor="GREY_MATTE"
        ml={190}
        ph={16}>
        {renderTimer('calendar')}
        <Spacer size={'SMALL'} direction="horizontal" />
        <Box justifyContent="center">
          <TextNew type="body12">{date}</TextNew>
        </Box>
      </Box>
    )
  }

  const onDateSelected = (selectedDate: Date) => {
    const parsedDate = toLocaleString(selectedDate)
    updateDate(parsedDate)
    toggleCalendarModal()
  }

  const toggleCalendarModal = () => {
    setCalendarVisible(!isCalendarVisible)
  }

  const renderTimerComp = () => {
    return (
      <Box
        {...styles.timerBox}
        underlayColor="TRANSPARENT"
        onLayout={(event) => handleLayoutChange(event, 'timer')}
        testID="tomer-ModalVisibility"
        onPress={() => setModalVisibility(true)}>
        {renderTimer('timer')}
        <Spacer size={'SMALL'} direction="horizontal" />
        <Box flexDirection="row" align="center">
          <TextNew type="body12">
            {returnFormattedTime(Number(hour))}:{returnFormattedTime(Number(minute))} {meridian}
          </TextNew>
        </Box>
      </Box>
    )
  }

  useEffect(() => {
    setFieldValue(fieldName, selectedTime)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, selectedTime])

  if (isExpiryChecked) {
    return null
  } else {
    return (
      <Box pl={'6%'} flexDirection="row">
        {renderCalendar()}
        {renderTimerComp()}
        {renderTimerPortal(setModalVisibility, showModal, layoutValue, rendeTimerDropdown)}
        {renderCalendarPortal(toggleCalendarModal, layoutValueCalendar, onDateSelected, isCalendarVisible)}
      </Box>
    )
  }
}

export { TimePicker }
